// Movement, local avoidance and arrival.
//
// Owns: vx, vy, facing, pathGoal, stuckTicks, moveState, arrivedBlocked, plus
// the private underscore scratch fields installed once per unit by initUnit().
//
// Reads: e.moveTarget = { x, y, stopDist } -- the "where do I want to be" hook
// that economy.js and combat.js write for gather/build/repair/chase orders.
// The hook is consumed and cleared every tick. For plain move / attack-move /
// patrol orders movement derives the target from the order itself.
//
// The tick is:
//   A   consume hooks, resolve order points to standable tiles, exact arrival
//   B   build the unit-only spatial index (cell size 2 tiles)
//   C   blob arrival: settle against settled group-mates, or on "cannot close"
//   D   decide per-unit steering source: straight line, or a shared flow field
//   D2  goal progress: the one signal that says whether a unit is getting closer
//   E   desired velocity (turn rate, attack-move cohesion)
//   F   separation steering
//   G   integrate + terrain, walk embedded units back out
//   H   overlap relaxation
//   I   stuck detection / sidestep / abandon an impossible order
//
// FOUR RULES THAT MAKE THIS FEEL LIKE STARCRAFT RATHER THAN A BOIDS DEMO
//
// 1. There is no acceleration. A move order sets full velocity on the very next
//    logic frame. Only SIZE.LARGE ground units get a turn rate, and only once
//    they are already moving, so a standing tank still leaves instantly.
//
// 2. Separation steers, it does not brake. The separation vector is added to a
//    unit-length desired direction and the sum is renormalised back to full
//    speed, with the separation contribution capped below 1.0. That cap is a
//    hard guarantee that the steered direction always keeps a positive
//    component along the desired direction: a unit in a crowd is deflected, it
//    is never stalled or pushed backwards. That single property is what gets 30
//    units through the main's single ramp in 5 seconds instead of jamming solid
//    in the mouth of it.
//
// 3. Arrival is contagious. The first unit to reach the order point settles;
//    every later unit that is inside the group's blob radius and touching a
//    settled group-mate settles too. The blob forms outward from the click,
//    nobody grinds against the pile, and settled units stop costing CPU. The
//    blob radius counts the units already parked on the goal as well as the
//    ones still walking to it -- a body takes up the same room whether or not
//    it is moving -- so the shell never shrinks out from under the stragglers
//    still trying to join it. When it is genuinely full, a unit pressed on the
//    outside of it stops there rather than pacing around it forever.
//
// 4. Steering, grouping and arrival all measure against the SAME point, and
//    that point is always somewhere a unit can physically stand. A click lands
//    on a building or a cliff constantly; if arrival is measured against the
//    raw click while steering aims at the nearest legal tile, the unit reaches
//    the only spot it can occupy and is told it is still three tiles short. It
//    then grinds on the obstacle forever. Everything that can permanently
//    prevent arrival -- an occupied goal, a crowded goal, a goal with no route
//    at all -- has an explicit answer here rather than an implicit timeout.

import { FlowFieldCache, sampleFlow, fieldDist, fieldReachable, lineClear } from './pathfinder.js';
import { TICK_DT, SEPARATION_STRENGTH, ARRIVE_EPSILON, STUCK_TICKS } from './constants.js';
import { nextOrder } from './commands.js';

const FIELD_BUDGET_PER_TICK = 3;   // cap fresh Dijkstra passes per logic frame
const LOS_MAX = 11;                // straight-shot test distance, tiles
const SEP_PAD = 0.55;              // personal space beyond touching, tiles
const SEP_CAP = 0.92;              // < 1.0: keeps steering from reversing a unit
const SEP_TANGENT = 0.30;          // swirl, so head-on pairs slide past each other
const SETTLED_WEIGHT = 1.7;        // how much harder moving units steer around settled ones
const TURN_RATE_LARGE = 0.55;      // rad/tick for SIZE.LARGE ground units
const SIDESTEP_TICKS = 10;
const STUCK_SAMPLE = 6;            // ticks between progress samples
const STUCK_MIN_PROGRESS = 0.16;   // tiles of progress required per sample
const NOGAIN_EPS = 0.05;           // path-distance gain that counts as progress
const NOGAIN_SETTLE = 14;          // ticks inside the blob without gaining -> stop
const NOGAIN_GIVEUP = 24;          // 1s of no gain just OUTSIDE a full blob -> stop
const GIVEUP_SLACK = 1.0;          // how far past the blob the give-up reaches, tiles
const UNREACH_TICKS = 6;           // consecutive ticks of "no route" -> drop the order
const PMODE_STRAIGHT = -1;         // goal-progress metric: straight line to the target
const PMODE_UNSET = -2;            // ...not yet sampled for this order
const COHESION_MIN_GROUP = 5;
const COHESION_LEAD = 2.5;         // tiles ahead of the group median before slowing
const COHESION_FLOOR = 0.6;        // never slower than 60% -- this must not read as sluggish

const MODE_NONE = 0;               // nothing to do this tick
const MODE_ORDER = 1;              // target came from move/attackmove/patrol: movement owns arrival
const MODE_HOOK = 2;               // target came from e.moveTarget: the writer owns arrival

const _flow = { x: 0, y: 0 };

// ---------------------------------------------------------------- scratch --
let _units = [];
let _tx = new Float64Array(0), _ty, _tstop, _tdist, _dirx, _diry, _spd;
let _mode = new Uint8Array(0);
let _ordKey = new Int32Array(0);
let _fgrp = [];
const _groups = new Map();         // order goal tile -> { n, hold, amove, dists, median }
const _fieldReq = new Map();       // resolved goal tile -> { gx, gy, count, field }
const _pending = [];

function ensure(n) {
  if (_tx.length >= n) return;
  const c = Math.max(64, 1 << (32 - Math.clz32(n)));
  _tx = new Float64Array(c); _ty = new Float64Array(c); _tstop = new Float64Array(c);
  _tdist = new Float64Array(c); _dirx = new Float64Array(c); _diry = new Float64Array(c);
  _spd = new Float64Array(c);
  _mode = new Uint8Array(c);
  _ordKey = new Int32Array(c);
  _fgrp = new Array(c).fill(null);
}

/**
 * A unit that must not be displaced by ANYTHING this tick -- not steering, not
 * the overlap solver, not a terrain escape. Siege mode's entire bargain is that
 * the tank trades mobility for range; a tank that can be nudged off its firing
 * position by a squad walking past is not sieged, it is just slow.
 */
function immobile(e) { return e.sieged === true || e.siegeTimer > 0; }

export function onGridChanged(world) { if (world._ff) world._ff.invalidate(); }

function fields(world) {
  if (!world._ff) world._ff = new FlowFieldCache(world.grid);
  return world._ff;
}

/**
 * Private per-unit state, installed in one fixed order the first time movement
 * sees a unit. Entities must stay monomorphic, so every unit walks the same
 * hidden-class chain -- never add one of these fields conditionally.
 */
function initUnit(e) {
  e._mv = 1;
  e._ord = e.order;
  e._lx = e.x; e._ly = e.y;
  e._settledGoal = -1;
  e._rawGoal = -1; e._resGoal = -1; e._goalVer = -1;
  e._sideTicks = 0; e._sideX = 0; e._sideY = 0;
  e._wake = 0;
  e._bestP = Infinity; e._noGain = 0; e._pMode = PMODE_UNSET; e._unreach = 0;
  e.arrivedBlocked = false;
}

/** A new order wipes the settle anchor and the sidestep, and re-bases progress. */
function onNewOrder(e) {
  e._ord = e.order;
  e._settledGoal = -1;
  e._sideTicks = 0;
  e._lx = e.x; e._ly = e.y;
  e._bestP = Infinity; e._noGain = 0; e._pMode = PMODE_UNSET; e._unreach = 0;
  e.arrivedBlocked = false;
}

/**
 * Snap an ORDER point to a tile a ground unit can actually stand on.
 *
 * A right-click lands wherever the mouse was, and in a real game that is very
 * often on top of a building, a mineral patch or a cliff face. Steering already
 * copes -- the flow field is built from the nearest legal tile -- but arrival
 * was still measured against the raw click, so the unit reached the only tile
 * it could occupy and was told it was still 2.9 tiles short. It then ground
 * against the obstacle for the rest of the match, vibrating across the goal
 * tile boundary, because the sidestep that was supposed to break the jam resets
 * the stuck counter that was supposed to end it.
 *
 * Resolving once, here, makes steering, grouping and arrival agree on a single
 * point -- which is the same lesson edgeDist() teaches for interaction ranges.
 * The resolution is cached against the raw tile and the grid version, so the
 * ring search runs once per order rather than once per unit per tick.
 */
function resolveGoal(world, e, tx, ty, out) {
  const g = world.grid, gw = g.w;
  let rx = tx | 0, ry = ty | 0;
  if (rx < 0) rx = 0; else if (rx >= gw) rx = gw - 1;
  if (ry < 0) ry = 0; else if (ry >= g.h) ry = g.h - 1;
  const raw = ry * gw + rx;
  if (e._rawGoal !== raw || e._goalVer !== g.version) {
    e._rawGoal = raw;
    e._goalVer = g.version;
    if (g.passable(rx, ry)) e._resGoal = raw;
    else { const s = g.nearestPassable(tx, ty); e._resGoal = (s.y | 0) * gw + (s.x | 0); }
  }
  const key = e._resGoal;
  if (key === raw) { out.x = tx; out.y = ty; }        // the click itself is legal: keep it exact
  else { const ky = (key / gw) | 0; out.x = key - ky * gw + 0.5; out.y = ky + 0.5; }
  return key;
}
const _goal = { x: 0, y: 0 };

// ------------------------------------------------------- unit spatial index --
// A units-only uniform grid at 2-tile resolution. The world hash is 4-tile and
// holds buildings and resources too, so in a 200-unit ball it hands back four
// times as many candidates as it needs to; movement queries it three times per
// unit per tick, which made it the second-largest cost after field builds.
const ICELL = 2;
let _ihead = null, _inext = new Int32Array(0), _icw = 0, _ich = 0;

function buildIndex(world, n) {
  const g = world.grid;
  const cw = Math.ceil(g.w / ICELL), ch = Math.ceil(g.h / ICELL);
  if (!_ihead || _ihead.length !== cw * ch) _ihead = new Int32Array(cw * ch);
  _ihead.fill(-1);
  if (_inext.length < n) _inext = new Int32Array(Math.max(64, n * 2));
  _icw = cw; _ich = ch;
  for (let i = 0; i < n; i++) {
    const e = _units[i];
    let cx = (e.x / ICELL) | 0, cy = (e.y / ICELL) | 0;
    if (cx < 0) cx = 0; else if (cx >= cw) cx = cw - 1;
    if (cy < 0) cy = 0; else if (cy >= ch) cy = ch - 1;
    const c = cy * cw + cx;
    _inext[i] = _ihead[c]; _ihead[c] = i;
  }
}

// ------------------------------------------------------------------- system --
export function movementSystem(world) {
  const ff = fields(world);
  const grid = world.grid;
  const gw = grid.w;
  const tick = world.tick;

  // collect units without allocating a fresh array per player per tick
  _units.length = 0;
  for (let p = 0; p < 2; p++) {
    for (const id of world.byPlayer[p]) {
      const e = world.entities.get(id);
      if (e && e.alive && e.kind === 'unit') _units.push(e);
    }
  }
  const n = _units.length;
  if (n === 0) return;
  ensure(n);
  _groups.clear();

  // ---- A. consume hooks, resolve targets, exact arrival -------------------
  for (let i = 0; i < n; i++) {
    const e = _units[i];
    if (e._mv !== 1) initUnit(e);
    if (e.order !== e._ord) onNewOrder(e);
    _fgrp[i] = null;
    _ordKey[i] = -1;

    // sieged tanks and units mid-transition never move, full stop
    if (e.sieged || e.siegeTimer > 0) {
      e.vx = 0; e.vy = 0; e.moveTarget = null; e.moveState = 'idle';
      e.stuckTicks = 0; _mode[i] = MODE_NONE; continue;
    }

    const hook = e.moveTarget;
    e.moveTarget = null;
    let tx, ty, stop, mode;
    if (hook) {
      tx = hook.x; ty = hook.y; stop = hook.stopDist || 0; mode = MODE_HOOK;
    } else {
      const o = e.order;
      if (o && (o.type === 'move' || o.type === 'attackmove')) { tx = o.x; ty = o.y; stop = 0; mode = MODE_ORDER; }
      else if (o && o.type === 'patrol') {
        if (o.leg === 0) { tx = o.x; ty = o.y; } else { tx = o.fromX; ty = o.fromY; }
        stop = 0; mode = MODE_ORDER;
      } else {
        e.vx = 0; e.vy = 0; e.moveState = 'idle'; e.stuckTicks = 0;
        _mode[i] = MODE_NONE; continue;
      }
      // Ground order points snap to somewhere a ground unit can stand. Flyers
      // skip this: they can and should hover directly over the click.
      if (!e.flying) { resolveGoal(world, e, tx, ty, _goal); tx = _goal.x; ty = _goal.y; }
    }

    const dx = tx - e.x, dy = ty - e.y;
    const straight = Math.sqrt(dx * dx + dy * dy);
    const eps = stop > ARRIVE_EPSILON ? stop : ARRIVE_EPSILON;

    // Group membership is counted BEFORE arrival, so the blob radius reflects
    // how many units were sent here, not how many are still walking. Counting
    // only the stragglers shrinks the blob as the group lands, which is exactly
    // backwards: the later a unit arrives, the more room it needs.
    let key = -1;
    if (mode === MODE_ORDER && e.order.type !== 'patrol') {
      let gx = tx | 0, gy = ty | 0;
      if (gx < 0) gx = 0; else if (gx >= gw) gx = gw - 1;
      if (gy < 0) gy = 0; else if (gy >= grid.h) gy = grid.h - 1;
      key = gy * gw + gx;
      let g = _groups.get(key);
      if (g === undefined) { g = { n: 0, hold: 0, amove: 0, dists: null, median: 0 }; _groups.set(key, g); }
      g.n++;
      if (e.order.type === 'attackmove') g.amove++;
    }
    _ordKey[i] = key;

    // The one exception to "arrival is the same measurement the order's owner
    // uses": a unit that physically cannot get closer (wedged against a
    // building or its own army) reports arrivedBlocked so the caller can finish
    // the order instead of shoving forever.
    if (straight <= eps) { settle(world, e, mode, key); _mode[i] = MODE_NONE; continue; }
    if (e.stuckTicks > STUCK_TICKS && straight <= stop + 1.6) {
      e.arrivedBlocked = true; settle(world, e, mode, key); _mode[i] = MODE_NONE; continue;
    }

    _tx[i] = tx; _ty[i] = ty; _tstop[i] = stop; _tdist[i] = straight; _mode[i] = mode;
    e.moveState = 'moving';
  }

  // ---- B. spatial index ---------------------------------------------------
  buildIndex(world, n);

  // ---- B2. units that already landed here still take up room --------------
  // Pass A counts a group before arrival so that the blob reflects how many
  // units were SENT here rather than how many are still walking -- but that
  // only covers the tick a unit lands on. A unit that settled on an earlier
  // tick has no order any more, so it drops out of the count entirely, and by
  // the time 49 of 50 riflemen are parked the group reads n=1 and the blob
  // shrinks from 4.1 tiles to 1.35 -- *around* the last unit still walking.
  // The shell it is trying to join is now defined as smaller than the pile of
  // bodies physically occupying it, so it can never be inside the blob, the
  // arrival rules below are gated off, and it paces the perimeter until the
  // stuck detector grinds it in tile by tile half a minute later.
  //
  // The bodies are the blob. Count the anchors settled on this goal, not just
  // the stragglers still walking toward it. `_ordKey[i] >= 0` means pass A
  // already counted this unit into g.n this tick, so it is skipped here rather
  // than counted twice.
  if (_groups.size !== 0) {
    for (let i = 0; i < n; i++) {
      if (_ordKey[i] >= 0) continue;
      const sg = _units[i]._settledGoal;
      if (sg < 0) continue;
      const g = _groups.get(sg);
      if (g !== undefined) g.hold++;
    }
  }

  // ---- C. blob arrival ----------------------------------------------------
  for (let i = 0; i < n; i++) {
    if (_mode[i] !== MODE_ORDER) continue;
    const key = _ordKey[i];
    if (key < 0) continue;
    const g = _groups.get(key);
    const blob = blobRadius(g);
    const e = _units[i];
    // Three ways to be done. Either a group-mate already stopped here and this
    // unit is touching it -- the blob grows outward from the click -- or this
    // unit has spent NOGAIN_SETTLE ticks inside the blob without ever getting
    // closer, which means the goal itself is taken: by a building, by a sieged
    // tank, or simply by the rest of the army. The second rule is what stops a
    // group orbiting its own destination forever. It cannot fire early on a
    // unit that is still closing, because closing is precisely what resets the
    // counter, and it cannot fire far from the goal, because the blob gates it.
    //
    // The third is the give-up, and it is the only one that reaches OUTSIDE the
    // blob -- by one body-length, into the band where a unit is pressed against
    // a shell that is completely full: every contact point taken, no way in,
    // and nothing to be gained by sliding around it. Judged on membership that
    // unit is not done and never will be, so it paces the perimeter. Judged on
    // progress it has been done for a second. A unit that could still get in
    // does get in, because getting closer is exactly what resets the counter.
    //
    // The band is measured from the goal, never from whatever the unit happens
    // to be touching, so the pile cannot chain outward: blob + slack is a hard
    // ceiling on how far from the click a unit may call itself arrived. It also
    // needs a landed group-mate (`hold`) before it opens at all, which keeps
    // "the destination is occupied" distinct from "the destination is merely
    // hard to reach" -- the latter is the unreachable-order path's job.
    const d = _tdist[i];
    const done = d <= blob
      ? (touchingSettled(e, key) || e._noGain >= NOGAIN_SETTLE)
      : (d <= blob + GIVEUP_SLACK && g.hold !== 0 && e._noGain >= NOGAIN_GIVEUP);
    if (!done) continue;
    settle(world, e, MODE_ORDER, key);
    _mode[i] = MODE_NONE;
  }

  // ---- D. steering source: straight line, or a shared flow field ----------
  _fieldReq.clear();
  for (let i = 0; i < n; i++) {
    if (_mode[i] === MODE_NONE) continue;
    const e = _units[i];
    const straight = _tdist[i];
    const dx = (_tx[i] - e.x) / straight, dy = (_ty[i] - e.y) / straight;
    _dirx[i] = dx; _diry[i] = dy;

    if (e.flying) continue;                       // flyers ignore terrain entirely

    const need = straight - _tstop[i];
    if (need <= LOS_MAX &&
        lineClear(grid, e.x, e.y, e.x + dx * need, e.y + dy * need, e.radius)) {
      e.pathGoal = null;
      continue;                                   // clear shot: no field needed
    }

    // Resolving the goal tile is a ring search when the goal is inside a
    // building or a mineral patch, which is the common case for gather and
    // repair (MODE_ORDER targets were already resolved in pass A, and hit the
    // same per-unit cache for free). Cache it against the raw goal tile and the
    // grid version.
    const key = resolveGoal(world, e, _tx[i], _ty[i], _goal);
    e.pathGoal = key;
    let r = _fieldReq.get(key);
    if (r === undefined) {
      r = { gx: key - ((key / gw) | 0) * gw, gy: (key / gw) | 0, count: 0, field: null };
      _fieldReq.set(key, r);
    }
    r.count++;
    _fgrp[i] = r;
  }

  // Serve every cached field for free, then spend the build budget on the
  // goals the most units are waiting on. Without the sort, a scout picking a
  // fresh goal each tick can starve a 40-unit attack of its field forever.
  _pending.length = 0;
  for (const r of _fieldReq.values()) {
    const f = ff.peek(r.gx, r.gy);
    if (f) r.field = f; else _pending.push(r);
  }
  if (_pending.length > 1) _pending.sort(byCountDesc);
  for (let k = 0; k < _pending.length && k < FIELD_BUDGET_PER_TICK; k++) {
    const r = _pending[k];
    r.field = ff.get(r.gx, r.gy);
  }

  // ---- D2. goal progress --------------------------------------------------
  // One number per unit: how far it still is from where it was told to be,
  // measured along the ACTUAL route when a field is available and straight-line
  // when it is not. The displacement-based stuck counter cannot answer this --
  // a unit orbiting its destination, or sidestepping along a wall, is moving
  // briskly and getting nowhere, which is why the sidestep (which resets the
  // stuck counter) made the old give-up threshold unreachable.
  //
  // Changing what is being measured re-bases rather than compares. Two
  // different fields, or a field and a straight line, are not on the same scale
  // -- field distance carries the obstacle-proximity penalty -- so carrying a
  // best-so-far across the switch reads as either a huge gain or a permanent
  // stall. `_pMode` is the identity of the current metric: the goal tile of the
  // field in use, or -1 for a straight line.
  for (let i = 0; i < n; i++) {
    if (_mode[i] === MODE_NONE) continue;
    const e = _units[i];
    const r = _fgrp[i];
    let src = PMODE_STRAIGHT, p = _tdist[i];
    if (r !== null && r.field !== null) {
      const fd = fieldDist(r.field, e.x, e.y);
      if (fd !== Infinity) { src = r.gy * gw + r.gx; p = fd; }
      e._unreach = fieldReachable(r.field, e.x, e.y) ? 0 : e._unreach + 1;
    } else {
      e._unreach = 0;
    }
    if (src !== e._pMode) { e._pMode = src; e._bestP = p; e._noGain = 0; }
    else if (p < e._bestP - NOGAIN_EPS) { e._bestP = p; e._noGain = 0; }
    else e._noGain++;
  }

  // ---- E. desired velocity ------------------------------------------------
  computeCohesion(world, n);

  for (let i = 0; i < n; i++) {
    if (_mode[i] === MODE_NONE) continue;
    const e = _units[i];
    let dirx = _dirx[i], diry = _diry[i];

    const r = _fgrp[i];
    if (r !== null && r.field !== null) {
      sampleFlow(r.field, e.x, e.y, _flow);
      if (_flow.x !== 0 || _flow.y !== 0) { dirx = _flow.x; diry = _flow.y; }
    }
    // r.field === null means the budget ran out this tick: coast on the
    // straight-line heading rather than freezing, and pick the field up next
    // tick. Never stall a unit on a bookkeeping limit.

    if (e._sideTicks > 0) {
      e._sideTicks--;
      dirx = dirx * 0.35 + e._sideX;
      diry = diry * 0.35 + e._sideY;
      const l = Math.sqrt(dirx * dirx + diry * diry) || 1;
      dirx /= l; diry /= l;
    }

    // SIZE.LARGE ground units arc instead of pivoting, but only while already
    // moving -- a standing tank still accelerates instantly in any direction.
    if (e.size === 2 && !e.flying) {
      const cur = e.vx * e.vx + e.vy * e.vy;
      if (cur > e.speed * e.speed * 0.16) {
        const a0 = Math.atan2(e.vy, e.vx);
        let d = Math.atan2(diry, dirx) - a0;
        if (d > Math.PI) d -= Math.PI * 2; else if (d < -Math.PI) d += Math.PI * 2;
        if (d > TURN_RATE_LARGE) { const a = a0 + TURN_RATE_LARGE; dirx = Math.cos(a); diry = Math.sin(a); }
        else if (d < -TURN_RATE_LARGE) { const a = a0 - TURN_RATE_LARGE; dirx = Math.cos(a); diry = Math.sin(a); }
      }
    }

    let spd = e.speed;
    // attack-move cohesion: units well ahead of their group's median progress
    // ease off so the army arrives as a lump instead of a single-file line.
    if (_mode[i] === MODE_ORDER && _ordKey[i] >= 0) {
      const g = _groups.get(_ordKey[i]);
      if (g.dists !== null) {
        const lead = g.median - progressOf(e, _fgrp[i], _tdist[i]);
        if (lead > COHESION_LEAD) {
          const f = 1 - (lead - COHESION_LEAD) * 0.05;
          spd *= f < COHESION_FLOOR ? COHESION_FLOOR : f;
        }
      }
    }

    _dirx[i] = dirx; _diry[i] = diry; _spd[i] = spd;
    e.vx = dirx * spd; e.vy = diry * spd;
  }

  // ---- F. separation ------------------------------------------------------
  for (let i = 0; i < n; i++) {
    if (_mode[i] === MODE_NONE) continue;
    const e = _units[i];
    const flying = e.flying;
    const reach = e.radius + 0.55 + SEP_PAD;
    let sx = 0, sy = 0;

    let cx0 = ((e.x - reach) / ICELL) | 0, cx1 = ((e.x + reach) / ICELL) | 0;
    let cy0 = ((e.y - reach) / ICELL) | 0, cy1 = ((e.y + reach) / ICELL) | 0;
    if (cx0 < 0) cx0 = 0; if (cy0 < 0) cy0 = 0;
    if (cx1 >= _icw) cx1 = _icw - 1; if (cy1 >= _ich) cy1 = _ich - 1;
    for (let cy = cy0; cy <= cy1; cy++) {
      const row = cy * _icw;
      for (let cx = cx0; cx <= cx1; cx++) {
        for (let j = _ihead[row + cx]; j !== -1; j = _inext[j]) {
          if (j === i) continue;
          const o = _units[j];
          if (o.flying !== flying) continue;      // air and ground never collide
          const dx = e.x - o.x, dy = e.y - o.y;
          const d2 = dx * dx + dy * dy;
          const lim = e.radius + o.radius + SEP_PAD;
          if (d2 >= lim * lim || d2 < 1e-9) continue;
          const d = Math.sqrt(d2);
          const ux = dx / d, uy = dy / d;
          let push = (lim - d) / lim;
          if (o.moveState !== 'moving') push *= SETTLED_WEIGHT;
          sx += ux * push - uy * push * SEP_TANGENT;
          sy += uy * push + ux * push * SEP_TANGENT;
        }
      }
    }

    if (sx !== 0 || sy !== 0) {
      const sm = Math.sqrt(sx * sx + sy * sy);
      // Cap the separation contribution strictly below the unit-length desired
      // direction, so dot(steered, desired) stays positive: crowds deflect a
      // unit, they can never stall it or push it backwards.
      const k = Math.min(SEP_CAP, sm * SEPARATION_STRENGTH * 0.25) / sm;
      const vx = _dirx[i] + sx * k, vy = _diry[i] + sy * k;
      const l = Math.sqrt(vx * vx + vy * vy);
      if (l > 1e-9) { e.vx = (vx / l) * _spd[i]; e.vy = (vy / l) * _spd[i]; }
    }
  }

  // ---- G. integrate + terrain --------------------------------------------
  for (let i = 0; i < n; i++) {
    const e = _units[i];
    // A building can be stamped on top of a unit -- canPlace() only tests the
    // grid, not units standing on it -- so ANY ground unit can end up embedded,
    // including an idle one with no order at all. Walking only the units that
    // happen to have somewhere to be leaves the idle ones welded inside the
    // wall forever, which is a permanently stuck unit by any definition.
    // Sieged/transitioning tanks are the one exception: they never move.
    if (!e.flying && !immobile(e) && !passableAt(world, e.x, e.y, e.radius)) {
      escape(world, e);
      continue;
    }
    if (e.vx === 0 && e.vy === 0) continue;
    const nx = e.x + e.vx * TICK_DT;
    const ny = e.y + e.vy * TICK_DT;
    if (e.flying) {
      e.x = clamp(nx, 1.5, world.grid.w - 1.5);
      e.y = clamp(ny, 1.5, world.grid.h - 1.5);
    } else {
      const okX = passableAt(world, nx, e.y, e.radius);
      const okY = passableAt(world, e.x, ny, e.radius);
      if (okX && okY && passableAt(world, nx, ny, e.radius)) { e.x = nx; e.y = ny; }
      else if (okX) { e.x = nx; }                 // slide along the wall
      else if (okY) { e.y = ny; }
    }
    if (e.vx > 1e-3 || e.vx < -1e-3 || e.vy > 1e-3 || e.vy < -1e-3) e.facing = Math.atan2(e.vy, e.vx);
  }

  // ---- H. overlap relaxation ---------------------------------------------
  buildIndex(world, n);
  const fullPass = (tick & 7) === 0;
  resolveOverlaps(world, n, tick, fullPass);
  resolveOverlaps(world, n, tick, fullPass);

  // ---- I. stuck detection -------------------------------------------------
  for (let i = 0; i < n; i++) {
    const e = _units[i];
    if (_mode[i] === MODE_NONE) { e.stuckTicks = 0; e._lx = e.x; e._ly = e.y; continue; }

    if (tick % STUCK_SAMPLE === (e.id % STUCK_SAMPLE)) {
      const mx = e.x - e._lx, my = e.y - e._ly;
      if (mx * mx + my * my < STUCK_MIN_PROGRESS * STUCK_MIN_PROGRESS) e.stuckTicks += STUCK_SAMPLE;
      else e.stuckTicks = 0;
      e._lx = e.x; e._ly = e.y;
    }

    const o = e.order;
    const owned = _mode[i] === MODE_ORDER && o && (o.type === 'move' || o.type === 'attackmove');

    // No route exists, full stop -- the flow field is a complete reachability
    // answer for the whole map, so this is not a guess. Walled off behind an
    // enemy's buildings, or told to walk onto an island: the order is
    // impossible, so drop it and take the next queued one. Debounced, because
    // a unit that a building was just stamped on top of reads unreachable for
    // the tick or two it takes pass G to walk it back out.
    if (owned && e._unreach > UNREACH_TICKS) {
      nextOrder(e); e._ord = e.order; e._settledGoal = -1;
      e.stuckTicks = 0; e._noGain = 0; e._bestP = Infinity; e._pMode = PMODE_UNSET; e._unreach = 0;
      e.vx = 0; e.vy = 0; e.moveState = 'idle';
      _mode[i] = MODE_NONE;
      continue;
    }
    if (e.stuckTicks <= STUCK_TICKS) continue;

    if (owned) {
      const g = _ordKey[i] >= 0 ? _groups.get(_ordKey[i]) : null;
      const blob = g ? blobRadius(g) : 1.6;
      if (_tdist[i] <= blob + 1.4) {              // close enough, call it arrived
        settle(world, e, MODE_ORDER, _ordKey[i]);
        _mode[i] = MODE_NONE;
        continue;
      }
    }
    // Break the jam by committing to a lateral direction for a few ticks. The
    // old behaviour teleported the unit 0.25 tiles along a random heading with
    // no passability check, which could shove it inside a cliff.
    if (e._sideTicks <= 0) startSidestep(world, e, _dirx[i], _diry[i]);
  }

  world.reindex();
}

function byCountDesc(a, b) { return b.count - a.count; }

/**
 * How wide the pile at a move goal is allowed to get, in tiles.
 *
 * `n` is the units still walking here, `hold` the ones already parked here. It
 * has to be the sum: n discs of radius r need a disc of radius ~r*sqrt(n/0.9),
 * and a unit does not stop taking up space the moment it stops moving. Reading
 * either half alone shrinks the blob as the group lands, which is exactly
 * backwards -- the later a unit arrives, the more room it needs.
 */
function blobRadius(g) { return 0.9 + 0.45 * Math.sqrt(g.n + g.hold); }

// ------------------------------------------------------------- arrival ------
/**
 * Stop here. For MODE_HOOK the writer (economy/combat) owns order completion --
 * movement must never advance those orders, or a worker that stops one tile
 * short of its patch loses its gather order and freezes forever.
 */
function settle(world, e, mode, key) {
  e.vx = 0; e.vy = 0;
  e.moveState = 'idle';
  e.stuckTicks = 0;
  e._sideTicks = 0;
  if (mode !== MODE_ORDER) return;
  const o = e.order;
  if (!o) return;
  if (o.type === 'patrol') { o.leg = o.leg === 0 ? 1 : 0; e._bestP = Infinity; e._noGain = 0; e._pMode = PMODE_UNSET; return; }
  // `key` is the RESOLVED goal tile the caller grouped this unit under, not the
  // raw order point. The two differ whenever the click landed on a building,
  // and a settled unit filed under the wrong key is an anchor nobody can find.
  const nxt = nextOrder(e);
  e._ord = e.order;
  // Anchor the blob so later arrivals have something to settle against, unless
  // a queued order took over.
  e._settledGoal = nxt ? -1 : key;
  e._lx = e.x; e._ly = e.y;
  e._bestP = Infinity; e._noGain = 0; e._pMode = PMODE_UNSET; e._unreach = 0;
}

/** Is `e` touching a settled member of the same move group? */
function touchingSettled(e, key) {
  const flying = e.flying;
  const reach = e.radius + 0.6 + 0.35;
  let cx0 = ((e.x - reach) / ICELL) | 0, cx1 = ((e.x + reach) / ICELL) | 0;
  let cy0 = ((e.y - reach) / ICELL) | 0, cy1 = ((e.y + reach) / ICELL) | 0;
  if (cx0 < 0) cx0 = 0; if (cy0 < 0) cy0 = 0;
  if (cx1 >= _icw) cx1 = _icw - 1; if (cy1 >= _ich) cy1 = _ich - 1;
  for (let cy = cy0; cy <= cy1; cy++) {
    const row = cy * _icw;
    for (let cx = cx0; cx <= cx1; cx++) {
      for (let j = _ihead[row + cx]; j !== -1; j = _inext[j]) {
        const o = _units[j];
        if (o === e || o._settledGoal !== key || o.flying !== flying || o.owner !== e.owner) continue;
        const dx = e.x - o.x, dy = e.y - o.y;
        const lim = e.radius + o.radius + 0.30;
        if (dx * dx + dy * dy <= lim * lim) return true;
      }
    }
  }
  return false;
}

// ------------------------------------------------------------ cohesion ------
function progressOf(e, r, straight) {
  if (r !== null && r.field !== null) {
    const d = fieldDist(r.field, e.x, e.y);
    if (d !== Infinity) return d;
  }
  return straight;
}

/** Median remaining distance per attack-move group, for the cohesion throttle. */
function computeCohesion(world, n) {
  for (const g of _groups.values()) { g.dists = null; g.median = 0; }
  let any = false;
  for (const g of _groups.values()) if (g.amove >= COHESION_MIN_GROUP) { g.dists = []; any = true; }
  if (!any) return;
  for (let i = 0; i < n; i++) {
    if (_mode[i] !== MODE_ORDER || _ordKey[i] < 0) continue;
    const e = _units[i];
    if (!e.order || e.order.type !== 'attackmove') continue;
    const g = _groups.get(_ordKey[i]);
    if (g.dists === null) continue;
    g.dists.push(progressOf(e, _fgrp[i], _tdist[i]));
  }
  for (const g of _groups.values()) {
    if (g.dists === null) continue;
    if (g.dists.length < COHESION_MIN_GROUP) { g.dists = null; continue; }
    g.dists.sort(numAsc);
    g.median = g.dists[g.dists.length >> 1];
  }
}
function numAsc(a, b) { return a - b; }

// ------------------------------------------------------------- geometry -----
const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);

function passableAt(world, x, y, r) {
  const g = world.grid;
  const p = r * 0.72;
  const x0 = Math.floor(x - p), x1 = Math.floor(x + p);
  const y0 = Math.floor(y - p), y1 = Math.floor(y + p);
  for (let ty = y0; ty <= y1; ty++)
    for (let tx = x0; tx <= x1; tx++)
      if (!g.passable(tx, ty)) return false;
  return true;
}

/** Walk a unit that is standing inside terrain out to the nearest legal tile. */
function escape(world, e) {
  const spot = world.grid.nearestPassable(e.x, e.y, 12);
  const tx = spot.x + 0.5, ty = spot.y + 0.5;
  const dx = tx - e.x, dy = ty - e.y;
  const d = Math.sqrt(dx * dx + dy * dy);
  if (d < 1e-6) return;
  const step = Math.min(d, e.speed * TICK_DT * 2);
  e.x += (dx / d) * step;
  e.y += (dy / d) * step;
  e.facing = Math.atan2(dy, dx);
  e._wake = world.tick + 3;
  // Being inside a footprint reads as "no route to the goal" for as long as the
  // walk-out takes, and a deep embed takes several ticks. Do not let that count
  // toward abandoning the order.
  e._unreach = 0;
}

function startSidestep(world, e, dirx, diry) {
  const l = Math.sqrt(dirx * dirx + diry * diry) || 1;
  const fx = dirx / l, fy = diry / l;
  // Deterministic per unit, but seeded from world.rng so a jammed column
  // splits both ways instead of every unit picking the same side.
  const left = world.rng.next() < 0.5;
  const px = left ? -fy : fy, py = left ? fx : -fx;
  const probe = e.radius + 1.2;
  let sx = px, sy = py;
  if (!e.flying && !passableAt(world, e.x + px * probe, e.y + py * probe, e.radius)) {
    if (passableAt(world, e.x - px * probe, e.y - py * probe, e.radius)) { sx = -px; sy = -py; }
  }
  e._sideX = sx; e._sideY = sy;
  e._sideTicks = SIDESTEP_TICKS;
  e.stuckTicks = 0;
}

// -------------------------------------------------------- overlap solver ----
/**
 * Push interpenetrating units apart. Only units that are moving (or were
 * displaced in the last few ticks, or just spawned) drive the search, so a
 * settled army and a saturated mineral line cost nothing; every eighth tick
 * runs the full set so a pair that went quiet while overlapped still resolves.
 */
function resolveOverlaps(world, n, tick, fullPass) {
  for (let i = 0; i < n; i++) {
    const e = _units[i];
    const active = e.moveState === 'moving' || e._wake >= tick || tick - e.spawnTick < 24;
    if (!active && !fullPass) continue;
    const flying = e.flying;
    const reach = e.radius + 0.6;
    let cx0 = ((e.x - reach) / ICELL) | 0, cx1 = ((e.x + reach) / ICELL) | 0;
    let cy0 = ((e.y - reach) / ICELL) | 0, cy1 = ((e.y + reach) / ICELL) | 0;
    if (cx0 < 0) cx0 = 0; if (cy0 < 0) cy0 = 0;
    if (cx1 >= _icw) cx1 = _icw - 1; if (cy1 >= _ich) cy1 = _ich - 1;
    for (let cy = cy0; cy <= cy1; cy++) {
      const row = cy * _icw;
      for (let cx = cx0; cx <= cx1; cx++) {
        for (let j = _ihead[row + cx]; j !== -1; j = _inext[j]) {
          if (j === i) continue;
          const o = _units[j];
          if (o.flying !== flying) continue;
          // handle each pair once: skip when the partner will drive it itself
          if (j < i) {
            const oActive = o.moveState === 'moving' || o._wake >= tick || tick - o.spawnTick < 24;
            if (oActive || fullPass) continue;
          }
          let dx = o.x - e.x, dy = o.y - e.y;
          let d2 = dx * dx + dy * dy;
          const rr = e.radius + o.radius;
          if (d2 >= rr * rr) continue;
          let d = Math.sqrt(d2);
          if (d < 1e-4) {
            const a = (e.id * 2.399963) % (Math.PI * 2);
            dx = Math.cos(a); dy = Math.sin(a); d = 1e-4;
          }
          const overlap = rr - d;
          const ux = dx / d, uy = dy / d;
          // A sieged tank is not "hard to push", it is immovable: the whole
          // point of siege mode is that it has given up mobility for range.
          // Letting the overlap solver shave a tenth of a tile off it per pair
          // per tick let a passing squad walk a sieged tank half a tile off its
          // firing position, which silently changes what it covers.
          const eFixed = immobile(e), oFixed = immobile(o);
          if (eFixed && oFixed) continue;
          let we;
          if (eFixed) we = 0;
          else if (oFixed) we = 1;
          else {
            // a moving unit yields nearly all the ground to a settled one, which
            // keeps mineral lines and holding armies from being shoved apart by
            // an army walking through them
            const eStatic = e.moveState !== 'moving', oStatic = o.moveState !== 'moving';
            we = eStatic && !oStatic ? 0.1 : (!eStatic && oStatic ? 0.9 : 0.5);
          }
          moveOut(world, e, -ux * overlap * we, -uy * overlap * we, tick);
          moveOut(world, o, ux * overlap * (1 - we), uy * overlap * (1 - we), tick);
        }
      }
    }
  }
}

function moveOut(world, e, dx, dy, tick) {
  if (dx === 0 && dy === 0) return;
  if (e.flying) { e.x += dx; e.y += dy; e._wake = tick + 3; return; }
  if (passableAt(world, e.x + dx, e.y + dy, e.radius)) { e.x += dx; e.y += dy; }
  else if (passableAt(world, e.x + dx, e.y, e.radius)) e.x += dx;
  else if (passableAt(world, e.x, e.y + dy, e.radius)) e.y += dy;
  else return;
  e._wake = tick + 3;
}
